"use client";

/**
 * Deck parse progress bar: displays reading/summarising/finishing phases
 * with batch counts, percent complete, and accessible progress attributes.
 */

import type { DeckParseProgressCounts } from "@/lib/onboarding/deckParseProgress";

// Phase bands leave headroom above/below so the bar never reads 0% or 100%
// while work remains: reading fills 5-80%, summarising/finishing are fixed
// checkpoints past it since their own work has no countable batches.
const READING_FLOOR = 5;
const READING_CEILING = 80;
const SUMMARISING_PERCENT = 85;
const FINISHING_PERCENT = 95;

export function deckParseProgressPercent(p: DeckParseProgressCounts): number {
  if (p.phase === "summarising") return SUMMARISING_PERCENT;
  if (p.phase === "finishing") return FINISHING_PERCENT;
  if (p.batchesTotal <= 0) return READING_FLOOR;
  // Clamp: a stale total from a retry can read lower than batchesDone already
  // reported, which must never push the bar past the reading band's ceiling.
  const ratio = Math.min(p.batchesDone, p.batchesTotal) / p.batchesTotal;
  return READING_FLOOR + ratio * (READING_CEILING - READING_FLOOR);
}

function deckParseProgressLabel(p: DeckParseProgressCounts): string {
  if (p.phase === "summarising") return "Summarising your deck…";
  if (p.phase === "finishing") return "Almost done…";
  if (p.batchesTotal <= 0) return "Reading your deck…";
  return `Reading your deck… ${p.batchesDone}/${p.batchesTotal}`;
}

export function DeckParseProgressBar({ progress }: { progress: DeckParseProgressCounts }) {
  const percent = deckParseProgressPercent(progress);
  const label = deckParseProgressLabel(progress);

  return (
    <div className="px-6 pt-4 pb-2" data-testid="deck-parse-progress">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-landing-secondary">{label}</span>
        <span className="text-xs font-medium text-landing-dark">{Math.round(percent)}%</span>
      </div>
      <div className="h-1.5 bg-landing-bg-medium/50 rounded-full overflow-hidden">
        <div
          className="h-full bg-lev-orange rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        />
      </div>
    </div>
  );
}
